import moment from 'moment'

// The published URL spec is MM/DD/YYYY, but vendors send unpadded parts and ISO dates too, so
// accept those shapes. Every one is parsed strictly, which keeps "13/45/1990", "25/12/2005" and
// other junk rejected rather than silently coerced into the wrong day.
export const PMS_BIRTH_DATE_FORMATS = ['MM/DD/YYYY', 'M/D/YYYY', 'M/DD/YYYY', 'MM/D/YYYY', 'YYYY-MM-DD']

const DAY_FORMAT = 'YYYY-MM-DD'

export const PmsMatchOutcomes = {
  Open: 'open',
  Create: 'create',
  Ambiguous: 'ambiguous'
}

/**
 * Trim, collapse internal whitespace and casefold, so that "  jOHn   DOE " and "John Doe"
 * compare equal.
 */
export const normalizePatientName = name => (name || '').normalize('NFC').trim().replace(/\s+/g, ' ').toLowerCase()

/**
 * Parse the MM/DD/YYYY birth date carried by the PMS quick-link URL down to a calendar day.
 * Returns null when absent or malformed — callers treat that as "no DOB supplied".
 */
export const parsePmsBirthDate = birthDate => {
  if (!birthDate) {
    return null
  }

  const parsed = moment(birthDate, PMS_BIRTH_DATE_FORMATS, true)

  return parsed.isValid() ? parsed.format(DAY_FORMAT) : null
}

/**
 * PatientSearchModel.birthdate is an AWSDateTime, so compare on the calendar day only —
 * a stored time component must not defeat an otherwise matching date.
 *
 * Read in UTC, not local time: a birthdate stored as midnight UTC would otherwise roll back to
 * the previous day for any viewer west of it, so the same patient would match or not depending
 * on where the browser is.
 */
const toCalendarDay = birthdate => {
  if (!birthdate) {
    return null
  }

  const parsed = moment.utc(birthdate)

  return parsed.isValid() ? parsed.format(DAY_FORMAT) : null
}

/**
 * A candidate may carry its name split into parts, whole, or both. Compare against either
 * representation so a record holding only one of them still matches. Unicode is normalized to NFC
 * so that a decomposed "José" matches a composed one.
 */
const candidateNames = candidate =>
  [[candidate?.firstName, candidate?.lastName].filter(Boolean).join(' '), candidate?.name]
    .filter(Boolean)
    .map(normalizePatientName)
    .filter(Boolean)

/**
 * Classify the candidates returned by the patient search into exact matches and suggestions,
 * and decide what the combined `createoradd` PMS action should do.
 *
 * Retrieval is fuzzy (prefix-based) but this decision is strict: a candidate is exact only when
 * its name matches, and — when the URL supplied a DOB — its own DOB confirms it. A missing or
 * conflicting candidate DOB demotes it to a suggestion rather than guessing.
 *
 * A DOB that is present but unparseable fails closed: nothing is exact, so the caller shows the
 * disambiguation popup. Treating it as "no DOB supplied" would silently drop the confirmation
 * step and could open the wrong patient's chart for any vendor sending an unexpected format.
 *
 * `resultsTruncated` says the search hit its page limit. Uniqueness cannot be proven from a
 * partial page — a second equally exact patient may sit outside it — so a lone exact match is
 * demoted to the popup rather than opened.
 *
 * Both name parts are required for exactness. A blank `PatientLastName` — a vendor template glitch
 * or an empty PMS field — still yields a usable one-token search term, and records leads are
 * routinely stored with a first name and no last name, so a single token could otherwise compare
 * exact against a lead and open a different patient's chart.
 *
 * Archived candidates are never exact. The patients list excludes them from search entirely; here
 * they stay visible as suggestions so the doctor can see the namesake, but a lone archived match
 * must not be opened as though it were the patient the PMS asked for.
 */
export const classifyPmsPatientMatches = ({ firstName, lastName, birthDate, candidates, resultsTruncated = false }) => {
  const allCandidates = candidates || []
  const requestedName = normalizePatientName(`${firstName || ''} ${lastName || ''}`)
  const requestedDay = parsePmsBirthDate(birthDate)
  const hasUnusableBirthDate = !!String(birthDate ?? '').trim() && !requestedDay

  const hasBothNameParts = !!normalizePatientName(firstName) && !!normalizePatientName(lastName)

  const exactMatches =
    requestedName && hasBothNameParts && !hasUnusableBirthDate
      ? allCandidates.filter(candidate => {
          if (candidate?.statusType === 'archived') {
            return false
          }

          if (!candidateNames(candidate).includes(requestedName)) {
            return false
          }

          return requestedDay ? toCalendarDay(candidate?.birthdate) === requestedDay : true
        })
      : []

  if (!allCandidates.length) {
    return {
      outcome: PmsMatchOutcomes.Create,
      matchedPatient: null,
      exactMatches: [],
      suggestions: []
    }
  }

  if (exactMatches.length === 1 && !resultsTruncated) {
    return {
      outcome: PmsMatchOutcomes.Open,
      matchedPatient: exactMatches[0],
      exactMatches,
      suggestions: []
    }
  }

  return {
    outcome: PmsMatchOutcomes.Ambiguous,
    matchedPatient: null,
    exactMatches,
    suggestions: allCandidates
  }
}
